import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Sword, LogOut, Settings as SettingsIcon, Trash2, Crown, X, ChevronLeft, RefreshCw, ImagePlus, Globe, Map, ArrowLeft, Users, Clock, Loader2, Library } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { SupportChat } from '../components/SupportChat';
import CanonLibraryModal from '../components/CanonLibraryModal';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UNIVERSES = [
  { id: 'starwars', name: 'Star Wars', image: 'https://customer-assets.emergentagent.com/job_41857d1d-1d1e-47df-90b3-d2aa6c5d2539/artifacts/ywfgiehf_image.png', pos: 'top', description: 'A galaxy far, far away', color: 'amber' },
  { id: 'got', name: 'Game of Thrones', image: 'https://static.prod-images.emergentagent.com/jobs/41857d1d-1d1e-47df-90b3-d2aa6c5d2539/images/832dd0d28f5042d868cd4082e27fe3ee187ee0fd23fae8beb018db4656c0c2a3.png', pos: '50% 30%', description: 'Westeros awaits', color: 'red' },
  { id: 'lotr', name: 'Lord of the Rings', image: 'https://static.prod-images.emergentagent.com/jobs/41857d1d-1d1e-47df-90b3-d2aa6c5d2539/images/b0ea9e37a8fd385713e663486a95ff78dd6307b918cc294ea81ce8c0b9927a14.png', pos: 'center', description: 'Middle-earth', color: 'emerald' },
  { id: 'wh40k', name: 'Warhammer 40K', image: 'https://static.prod-images.emergentagent.com/jobs/41857d1d-1d1e-47df-90b3-d2aa6c5d2539/images/146e5a6bfde7411e9b0270d1a1731dc28e9ee069c21e3d38fa67ecdd8eada52b.png', pos: 'top', description: 'Grimdark future', color: 'red' },
];

// Single source of truth for universes that should be gated everywhere.
// Adding/removing one here ripples through Dashboard cards, Canon Library
// tabs, and backend safety checks (which read the same set independently).
const UNDER_DEVELOPMENT_UNIVERSES = new Set(
  UNIVERSES.filter(u => u.under_development).map(u => u.id)
);

function getTierColor(tier) {
  const colors = { free: 'bg-slate-700 text-slate-300', tier_5: 'bg-green-900/40 text-green-400 border border-green-500/20', tier_10: 'bg-blue-900/40 text-blue-400 border border-blue-500/20', tier_15: 'bg-purple-900/40 text-purple-400 border border-purple-500/20', tier_25: 'bg-amber-900/40 text-amber-400 border border-amber-500/20', tier_35: 'bg-red-900/40 text-red-400 border border-red-500/20' };
  return colors[tier] || colors.free;
}

function getTierDisplay(tier) {
  const names = { free: 'Free', tier_5: 'Adventurer', tier_10: 'Hero', tier_15: 'Champion', tier_25: 'Warrior', tier_35: 'Legend' };
  return names[tier] || 'Free';
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();

  // Navigation state: null = universe grid, 'starwars' = world list, 'world_xxx' = character list
  const [selectedUniverse, setSelectedUniverse] = useState(null);
  const [selectedWorld, setSelectedWorld] = useState(null);

  const [worlds, setWorlds] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subInfo, setSubInfo] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // World creation
  const [showCreateWorld, setShowCreateWorld] = useState(false);
  const [newWorldName, setNewWorldName] = useState('');
  const [newWorldDesc, setNewWorldDesc] = useState('');
  const [newWorldTimeline, setNewWorldTimeline] = useState('');
  const [creatingWorld, setCreatingWorld] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Portrait
  const [generatingPortraitId, setGeneratingPortraitId] = useState(null);
  const [dashImageModel, setDashImageModel] = useState('openai');

  // Orphan characters (no world_id) — from the pre-world era
  const [orphans, setOrphans] = useState([]);
  const [assignTarget, setAssignTarget] = useState(null); // character being assigned
  const [assignMode, setAssignMode] = useState('existing'); // 'existing' or 'new'
  const [assignExistingWorldId, setAssignExistingWorldId] = useState('');
  const [assignNewWorldName, setAssignNewWorldName] = useState('');
  const [assignNewWorldDesc, setAssignNewWorldDesc] = useState('');
  const [assignNewWorldTimeline, setAssignNewWorldTimeline] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [worldsForUniverse, setWorldsForUniverse] = useState([]); // cached when modal opens

  // Canon Library modal
  const [canonModalOpen, setCanonModalOpen] = useState(false);
  const [canonFilterUniverse, setCanonFilterUniverse] = useState(null);

  const openCanonLibrary = (universe = null) => {
    setCanonFilterUniverse(universe);
    setCanonModalOpen(true);
  };

  useEffect(() => {
    fetchSubscriptionInfo();
    fetchCampaigns();
    fetchOrphans();
  }, []);

  useEffect(() => {
    if (selectedUniverse) {
      fetchWorlds(selectedUniverse);
    }
  }, [selectedUniverse]);

  const fetchSubscriptionInfo = async () => {
    try {
      const res = await axios.get(`${API}/subscription/info`, { withCredentials: true });
      setSubInfo(res.data);
    } catch {}
  };

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/campaigns`, { withCredentials: true });
      setCampaigns(res.data);
    } catch {}
  };

  const fetchOrphans = async () => {
    try {
      const res = await axios.get(`${API}/characters`, { withCredentials: true });
      const orphaned = (res.data || []).filter(c => !c.world_id);
      setOrphans(orphaned);
    } catch {}
  };

  const openAssignModal = async (character) => {
    setAssignTarget(character);
    setAssignMode('existing');
    setAssignExistingWorldId('');
    setAssignNewWorldName('');
    setAssignNewWorldDesc('');
    setAssignNewWorldTimeline('');
    // Fetch worlds in this character's universe so the user can pick one
    try {
      const res = await axios.get(`${API}/worlds?universe=${character.universe}`, { withCredentials: true });
      setWorldsForUniverse(res.data || []);
      // If no worlds exist in this universe, default to "create new" mode
      if (!res.data || res.data.length === 0) setAssignMode('new');
    } catch {
      setWorldsForUniverse([]);
      setAssignMode('new');
    }
  };

  const submitAssign = async () => {
    if (!assignTarget) return;
    setAssigning(true);
    try {
      let payload;
      if (assignMode === 'existing') {
        if (!assignExistingWorldId) {
          toast.error('Please pick a world');
          setAssigning(false);
          return;
        }
        payload = { world_id: assignExistingWorldId };
      } else {
        if (!assignNewWorldName.trim()) {
          toast.error('Please give your new world a name');
          setAssigning(false);
          return;
        }
        payload = {
          new_world: {
            name: assignNewWorldName.trim(),
            description: assignNewWorldDesc.trim(),
            timeline_position: assignNewWorldTimeline.trim(),
          }
        };
      }
      await axios.post(
        `${API}/characters/${assignTarget.character_id}/assign-world`,
        payload,
        { withCredentials: true }
      );
      toast.success(`${assignTarget.name} assigned to a world`);
      setAssignTarget(null);
      await fetchOrphans();
      // If user is already browsing this universe, refresh worlds to show the new one
      if (selectedUniverse === assignTarget.universe) fetchWorlds(selectedUniverse);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign character');
    } finally {
      setAssigning(false);
    }
  };

  const fetchWorlds = async (universe) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/worlds?universe=${universe}`, { withCredentials: true });
      setWorlds(res.data);
    } catch (err) {
      toast.error('Failed to load worlds');
    } finally {
      setLoading(false);
    }
  };

  const createWorld = async () => {
    if (!newWorldName.trim()) return;
    setCreatingWorld(true);
    try {
      const res = await axios.post(`${API}/worlds`, {
        universe: selectedUniverse,
        name: newWorldName.trim(),
        description: newWorldDesc.trim(),
        timeline_position: newWorldTimeline.trim(),
      }, { withCredentials: true });
      toast.success(`World "${res.data.name}" created`);
      setShowCreateWorld(false);
      setNewWorldName('');
      setNewWorldDesc('');
      setNewWorldTimeline('');
      fetchWorlds(selectedUniverse);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create world');
    } finally {
      setCreatingWorld(false);
    }
  };

  const deleteWorld = async (worldId, worldName) => {
    try {
      await axios.delete(`${API}/worlds/${worldId}`, { withCredentials: true });
      toast.success(`World "${worldName}" deleted`);
      setSelectedWorld(null);
      fetchWorlds(selectedUniverse);
    } catch (err) {
      toast.error('Failed to delete world');
    }
  };

  const deleteCharacter = async (charId) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/characters/${charId}`, { withCredentials: true });
      toast.success('Character deleted');
      setDeleteTarget(null);
      if (selectedWorld) {
        // Refresh the world to get updated character list
        try {
          const res = await axios.get(`${API}/worlds/${selectedWorld.world_id}`, { withCredentials: true });
          setSelectedWorld(res.data);
        } catch {}
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete character');
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    logout();
    navigate('/');
  };

  const handleUpgrade = (tier) => {
    navigate('/settings', { state: { openUpgrade: tier } });
  };

  const launchCharacter = async (character) => {
    const charCampaigns = campaigns.filter(c => c.character_id === character.character_id);
    const latestCampaign = charCampaigns.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    if (latestCampaign) {
      navigate(`/game/${character.character_id}`, { state: { campaignId: latestCampaign.campaign_id } });
    } else {
      try {
        const response = await axios.post(`${API}/campaigns?character_id=${character.character_id}`, {}, { withCredentials: true });
        navigate(`/game/${character.character_id}`, { state: { campaignId: response.data.campaign_id } });
      } catch {
        toast.error('Failed to start campaign');
      }
    }
  };

  const universeData = UNIVERSES.find(u => u.id === selectedUniverse);

  // ═══ RENDER ═══

  return (
    <div className="min-h-screen bg-black" data-testid="dashboard">
      {/* Header */}
      <div className="glass-card border-b border-white/5 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                if (selectedWorld) { setSelectedWorld(null); }
                else if (selectedUniverse) { setSelectedUniverse(null); setWorlds([]); }
                else { navigate('/'); }
              }}
              className="text-blue-500/50 hover:text-blue-400 h-8 w-8"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-bold text-blue-400 font-mono tracking-wider">
              {selectedWorld ? selectedWorld.name : selectedUniverse ? universeData?.name : 'TaleMatrix'}
            </h1>
            {selectedWorld && (
              <span className="text-xs text-slate-500 font-mono">{selectedWorld.timeline_position}</span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {subInfo && (
              <Button
                onClick={() => navigate('/settings')}
                className={`${getTierColor(subInfo.subscription_tier)} font-bold font-mono text-xs hover:opacity-80`}
                data-testid="tier-badge"
              >
                <Crown className="h-3.5 w-3.5 mr-1.5" />
                {getTierDisplay(subInfo.subscription_tier)}
              </Button>
            )}
            <span className="text-slate-400 text-sm hidden sm:inline">{user?.name}</span>
            <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} className="text-slate-400 hover:text-blue-400 h-8 w-8" data-testid="settings-btn">
              <SettingsIcon className="h-4 w-4" />
            </Button>
            <SupportChat />
            {user?.email === 'nicoeconomakis@gmail.com' && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/support')} className="text-amber-400/60 hover:text-amber-400 text-xs font-mono" data-testid="admin-support-link">
                Inbox
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-slate-400 hover:text-red-500 h-8 w-8" data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={() => !deleting && setDeleteTarget(null)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} onClick={(e) => e.stopPropagation()} className="glass-card p-8 rounded-2xl max-w-md w-full text-center">
              <Trash2 className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-200 mb-2">Delete {deleteTarget.type === 'world' ? 'World' : 'Character'}</h3>
              <p className="text-slate-400 mb-6">
                Delete <span className="text-blue-400 font-bold">{deleteTarget.name}</span>{deleteTarget.type === 'world' ? ' and all its characters?' : ' and all their campaigns?'} This cannot be undone.
              </p>
              <div className="flex gap-3 justify-center">
                <Button variant="ghost" onClick={() => setDeleteTarget(null)} className="text-slate-400" disabled={deleting} data-testid="delete-cancel-btn">Cancel</Button>
                <Button onClick={() => deleteTarget.type === 'world' ? deleteWorld(deleteTarget.id, deleteTarget.name) : deleteCharacter(deleteTarget.id)} className="bg-red-500 hover:bg-red-600 text-white font-bold" disabled={deleting} data-testid="delete-confirm-btn">
                  {deleting ? 'Deleting...' : 'Delete Forever'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Assign Orphan to World Modal */}
      <AnimatePresence>
        {assignTarget && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={() => !assigning && setAssignTarget(null)}>
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9 }} onClick={(e) => e.stopPropagation()} className="glass-card rounded-2xl max-w-lg w-full overflow-hidden" data-testid="assign-modal">
              {/* Character header */}
              <div className="flex items-center gap-4 p-5 border-b border-white/5 bg-gradient-to-r from-amber-500/10 to-transparent">
                {assignTarget.portrait_url ? (
                  <img src={assignTarget.portrait_url} alt={assignTarget.name} className="w-14 h-14 rounded-lg object-cover border border-amber-500/30" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-slate-800 flex items-center justify-center border border-amber-500/30">
                    <Sword className="h-6 w-6 text-amber-400/60" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-slate-100 font-mono truncate">{assignTarget.name}</h3>
                  <p className="text-xs text-slate-400 font-mono truncate">
                    {assignTarget.race} {assignTarget.char_class} · {UNIVERSES.find(u => u.id === assignTarget.universe)?.name || assignTarget.universe}
                  </p>
                </div>
              </div>

              {/* Explainer */}
              <div className="px-5 py-3 bg-amber-500/5 border-b border-amber-500/10">
                <p className="text-xs text-amber-200/80 leading-relaxed">
                  This character was created before the World system existed. Assign them to a World to access them from the Dashboard.
                </p>
              </div>

              {/* Mode toggle */}
              <div className="flex p-4 gap-2">
                <button
                  onClick={() => setAssignMode('existing')}
                  disabled={worldsForUniverse.length === 0}
                  className={`flex-1 px-3 py-2 rounded font-mono text-xs transition-all ${assignMode === 'existing' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40' : 'bg-slate-800/50 text-slate-400 border border-transparent hover:border-white/10'} disabled:opacity-40 disabled:cursor-not-allowed`}
                  data-testid="assign-mode-existing"
                >
                  Add to Existing World
                  {worldsForUniverse.length === 0 && <span className="block text-[9px] text-slate-600 mt-0.5">none available</span>}
                </button>
                <button
                  onClick={() => setAssignMode('new')}
                  className={`flex-1 px-3 py-2 rounded font-mono text-xs transition-all ${assignMode === 'new' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-slate-800/50 text-slate-400 border border-transparent hover:border-white/10'}`}
                  data-testid="assign-mode-new"
                >
                  Create New World
                </button>
              </div>

              {/* Forms */}
              <div className="px-5 pb-5">
                {assignMode === 'existing' && worldsForUniverse.length > 0 && (
                  <div className="space-y-2">
                    {worldsForUniverse.map(w => (
                      <button
                        key={w.world_id}
                        onClick={() => setAssignExistingWorldId(w.world_id)}
                        className={`w-full text-left p-3 rounded-lg border transition-all flex items-center gap-3 ${assignExistingWorldId === w.world_id ? 'bg-blue-500/15 border-blue-400/50' : 'bg-slate-900/50 border-white/5 hover:border-blue-400/30'}`}
                        data-testid={`pick-world-${w.world_id}`}
                      >
                        {w.image_url && <img src={w.image_url} alt={w.name} className="w-10 h-10 rounded object-cover" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-slate-200 font-mono truncate">{w.name}</div>
                          <div className="text-xs text-slate-500 font-mono truncate">
                            {w.timeline_position || 'no timeline set'} · {(w.characters || []).length} character{(w.characters || []).length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {assignMode === 'new' && (
                  <div className="space-y-3">
                    <input
                      value={assignNewWorldName}
                      onChange={e => setAssignNewWorldName(e.target.value)}
                      placeholder="World name (e.g. Clone Wars Campaign)"
                      className="w-full bg-slate-900 border border-white/10 rounded px-3 py-2 text-sm text-slate-200 font-mono focus:border-blue-400/50 focus:outline-none"
                      data-testid="assign-new-name"
                      autoFocus
                    />
                    <textarea
                      value={assignNewWorldDesc}
                      onChange={e => setAssignNewWorldDesc(e.target.value)}
                      placeholder="Short description (optional)"
                      rows={2}
                      className="w-full bg-slate-900 border border-white/10 rounded px-3 py-2 text-sm text-slate-300 font-mono focus:border-blue-400/50 focus:outline-none resize-none"
                      data-testid="assign-new-desc"
                    />
                    <input
                      value={assignNewWorldTimeline}
                      onChange={e => setAssignNewWorldTimeline(e.target.value)}
                      placeholder={
                        assignTarget.universe === 'got' ? 'Starting timeline (e.g. 298 AC)'
                        : assignTarget.universe === 'starwars' ? 'Starting timeline (e.g. 22 BBY)'
                        : assignTarget.universe === 'lotr' ? 'Starting timeline (e.g. TA 3018)'
                        : assignTarget.universe === 'wh40k' ? 'Starting timeline (e.g. 999.M41)'
                        : 'Starting timeline (optional)'
                      }
                      className="w-full bg-slate-900 border border-white/10 rounded px-3 py-2 text-sm text-slate-300 font-mono focus:border-blue-400/50 focus:outline-none"
                      data-testid="assign-new-timeline"
                    />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 px-5 pb-5">
                <Button variant="ghost" onClick={() => setAssignTarget(null)} disabled={assigning} className="flex-1 text-slate-400" data-testid="assign-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={submitAssign}
                  disabled={assigning || (assignMode === 'existing' && !assignExistingWorldId) || (assignMode === 'new' && !assignNewWorldName.trim())}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-bold font-mono"
                  data-testid="assign-submit-btn"
                >
                  {assigning ? 'Assigning...' : assignMode === 'new' ? 'Create & Assign' : 'Assign to World'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">

        {/* ═══ ORPHAN CHARACTERS — legacy chars without a World ═══ */}
        {!selectedUniverse && orphans.length > 0 && (
          <div className="mb-8" data-testid="orphan-strip">
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-lg font-bold text-amber-300 font-mono">Unassigned Characters</h2>
              <span className="text-xs text-amber-400/60 font-mono">
                Click to assign them to a World
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              {orphans.map(char => {
                const universeMeta = UNIVERSES.find(u => u.id === char.universe);
                return (
                  <motion.button
                    key={char.character_id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={() => openAssignModal(char)}
                    className="glass-card rounded-lg border-2 border-dashed border-amber-500/30 hover:border-amber-400/70 transition-all text-left overflow-hidden group relative"
                    style={{ width: '180px' }}
                    data-testid={`orphan-${char.character_id}`}
                  >
                    {/* Badge: NEEDS WORLD */}
                    <div className="absolute top-1.5 right-1.5 z-10 px-1.5 py-0.5 rounded bg-amber-500/90 text-[8px] font-mono font-bold text-black tracking-wider">
                      NO WORLD
                    </div>
                    <div className="h-24 overflow-hidden relative">
                      {char.portrait_url ? (
                        <img src={char.portrait_url} alt={char.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
                          <Sword className="h-8 w-8 text-amber-400/40" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                    </div>
                    <div className="p-2.5">
                      <div className="text-sm font-bold text-slate-100 font-mono truncate">{char.name}</div>
                      <div className="text-[10px] text-amber-400/80 font-mono">{universeMeta?.name || char.universe}</div>
                      <div className="text-[10px] text-slate-500 font-mono truncate">{char.race} {char.char_class}</div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ LEVEL 1: Universe Selection ═══ */}
        {!selectedUniverse && (
          <div>
            {/* Canon Library call-out */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => openCanonLibrary(null)}
              className="w-full mb-6 glass-card rounded-lg border border-amber-500/20 hover:border-amber-400/50 p-4 flex items-center gap-4 group transition-all text-left bg-gradient-to-r from-amber-500/5 via-blue-500/5 to-purple-500/5"
              data-testid="open-canon-library-dashboard"
            >
              <div className="w-12 h-12 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/25 transition-all">
                <Library className="h-6 w-6 text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-amber-200 font-mono tracking-wide">Canon Library</h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Start a ready-made campaign — Star Wars, Game of Thrones, LOTR, Warhammer 40K. <span className="text-amber-300/80">Instant. Zero LLM cost.</span>
                </p>
              </div>
              <ArrowLeft className="h-4 w-4 text-amber-400/70 rotate-180 group-hover:translate-x-1 transition-transform" />
            </motion.button>

            <h2 className="text-2xl font-bold text-blue-300 font-mono mb-6">Choose Your Universe</h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {UNIVERSES.map((universe, i) => {
                const locked = !!universe.under_development;
                return (
                  <motion.button
                    key={universe.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => {
                      if (locked) {
                        toast.info(`${universe.name} is under development — Star Wars only for now.`);
                        return;
                      }
                      setSelectedUniverse(universe.id);
                    }}
                    className={`glass-card rounded-lg border transition-all text-left group overflow-hidden relative ${locked ? 'border-white/5 opacity-60 cursor-not-allowed' : 'border-white/5 hover:border-blue-400/40'}`}
                    data-testid={`universe-${universe.id}`}
                    aria-disabled={locked}
                  >
                    {locked && (
                      <div className="absolute inset-0 z-10 bg-black/55 backdrop-blur-[1px] flex flex-col items-center justify-center pointer-events-none">
                        <div className="px-3 py-1 rounded bg-amber-500/90 text-black font-mono font-bold text-[10px] tracking-wider mb-2">
                          UNDER DEVELOPMENT
                        </div>
                        <p className="text-xs text-slate-300 font-mono">Coming soon</p>
                      </div>
                    )}
                    {universe.image ? (
                      <div className="w-full h-32 overflow-hidden">
                        <img src={universe.image} alt={universe.name} style={{ objectPosition: universe.pos || 'center' }} className={`w-full h-full object-cover transition-transform duration-300 ${locked ? 'grayscale' : 'group-hover:scale-110'}`} />
                      </div>
                    ) : (
                      <div className="w-full h-32 bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
                        <Globe className="h-10 w-10 text-slate-600 group-hover:text-blue-400/50 transition-colors" />
                      </div>
                    )}
                    <div className="p-3">
                      <h3 className="text-sm font-bold text-slate-200 font-mono">{universe.name}</h3>
                      <p className="text-xs text-slate-500">{universe.description}</p>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ LEVEL 2: Worlds within Universe ═══ */}
        {selectedUniverse && !selectedWorld && (
          <div>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
              <h2 className="text-2xl font-bold text-blue-300 font-mono">
                Your Worlds
              </h2>
              <div className="flex gap-2">
                <Button
                  onClick={() => openCanonLibrary(selectedUniverse)}
                  className="bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 font-mono"
                  data-testid="open-canon-library-universe"
                >
                  <Library className="mr-2 h-4 w-4" />
                  Canon Library
                </Button>
                <Button
                  onClick={() => setShowCreateWorld(true)}
                  className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 font-mono"
                  data-testid="create-world-btn"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New World
                </Button>
              </div>
            </div>

            {/* Create World Form */}
            <AnimatePresence>
              {showCreateWorld && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6">
                  <div className="glass-card p-6 rounded-lg border border-blue-500/20 space-y-3">
                    <h3 className="text-sm font-mono font-bold text-blue-400">Create New World</h3>
                    <input
                      type="text"
                      value={newWorldName}
                      onChange={(e) => setNewWorldName(e.target.value)}
                      placeholder="World name (e.g. Clone Wars Era)"
                      className="w-full bg-slate-950/50 border border-white/10 text-slate-200 text-sm rounded-lg px-3 py-2 focus:border-blue-400/50 focus:outline-none"
                      data-testid="world-name-input"
                    />
                    <input
                      type="text"
                      value={newWorldTimeline}
                      onChange={(e) => setNewWorldTimeline(e.target.value)}
                      placeholder={selectedUniverse === 'got' ? 'Starting timeline (e.g. 298 AC)' : selectedUniverse === 'starwars' ? 'Starting timeline (e.g. 22 BBY)' : selectedUniverse === 'lotr' ? 'Starting timeline (e.g. TA 3018)' : selectedUniverse === 'wh40k' ? 'Starting timeline (e.g. 999.M41)' : 'Starting timeline (optional)'}
                      className="w-full bg-slate-950/50 border border-white/10 text-slate-200 text-sm rounded-lg px-3 py-2 focus:border-blue-400/50 focus:outline-none"
                      data-testid="world-timeline-input"
                    />
                    <textarea
                      value={newWorldDesc}
                      onChange={(e) => setNewWorldDesc(e.target.value)}
                      placeholder="Description (optional)"
                      rows={2}
                      className="w-full bg-slate-950/50 border border-white/10 text-slate-200 text-sm rounded-lg px-3 py-2 resize-none focus:border-blue-400/50 focus:outline-none"
                      data-testid="world-desc-input"
                    />
                    <div className="flex gap-2">
                      <Button onClick={createWorld} disabled={creatingWorld || !newWorldName.trim()} className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 font-mono" data-testid="world-create-submit">
                        {creatingWorld ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                        {creatingWorld ? 'Creating...' : 'Create'}
                      </Button>
                      <Button variant="ghost" onClick={() => setShowCreateWorld(false)} className="text-slate-400">Cancel</Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {loading ? (
              <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-400 mx-auto" /></div>
            ) : worlds.length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-12 rounded-lg text-center border border-white/5">
                <Map className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg mb-2 font-mono">No worlds yet</p>
                <p className="text-slate-500 text-sm mb-4">Create a world to start your adventure in {universeData?.name}</p>
                <Button onClick={() => setShowCreateWorld(true)} className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 font-mono">
                  <Plus className="mr-2 h-4 w-4" /> Create Your First World
                </Button>
              </motion.div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {worlds.map((world, i) => (
                  <motion.div
                    key={world.world_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="glass-card rounded-lg border border-white/5 hover:border-blue-400/30 transition-all overflow-hidden relative group"
                    data-testid={`world-${world.world_id}`}
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: world.world_id, name: world.name, type: 'world' }); }}
                      className="absolute top-2 right-2 p-1.5 rounded text-red-400/70 hover:text-red-400 bg-black/40 hover:bg-red-500/30 transition-all opacity-0 group-hover:opacity-100 z-10"
                      data-testid={`delete-world-${world.world_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>

                    <button
                      onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/worlds/${world.world_id}`, { withCredentials: true });
                          setSelectedWorld(res.data);
                        } catch { toast.error('Failed to load world'); }
                      }}
                      className="w-full text-left"
                    >
                      {/* Cover image */}
                      {world.image_url && (
                        <div className="relative h-32 overflow-hidden border-b border-white/5">
                          <img
                            src={world.image_url}
                            alt={world.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                          <div className="absolute bottom-2 left-3 right-3">
                            <h3 className="text-lg font-bold text-white font-mono drop-shadow-[0_2px_4px_rgba(0,0,0,0.95)]">{world.name}</h3>
                            {world.timeline_position && (
                              <span className="text-xs text-amber-300 font-mono flex items-center gap-1 mt-0.5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.95)]">
                                <Clock className="h-3 w-3" /> {world.timeline_position}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="p-4">
                        {/* Title fallback for worlds without images */}
                        {!world.image_url && (
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="text-lg font-bold text-slate-200 font-mono">{world.name}</h3>
                              {world.timeline_position && (
                                <span className="text-xs text-amber-400/70 font-mono flex items-center gap-1 mt-1">
                                  <Clock className="h-3 w-3" /> {world.timeline_position}
                                </span>
                              )}
                            </div>
                            <Map className="h-5 w-5 text-blue-400/30" />
                          </div>
                        )}
                        {world.description && (
                          <p className="text-slate-500 text-xs mb-3 line-clamp-2">{world.description}</p>
                        )}
                        <div className="flex items-center gap-2">
                          <Users className="h-3.5 w-3.5 text-slate-500" />
                          <span className="text-xs text-slate-400">
                            {(world.characters || []).length} character{(world.characters || []).length !== 1 ? 's' : ''}
                          </span>
                          {(world.characters || []).map(c => (
                            c.portrait_url ? (
                              <img key={c.character_id} src={c.portrait_url} alt={c.name} className="w-6 h-6 rounded-full border border-white/10 -ml-1" />
                            ) : (
                              <div key={c.character_id} className="w-6 h-6 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center -ml-1">
                                <span className="text-[9px] text-slate-400">{c.name?.[0]}</span>
                              </div>
                            )
                        ))}
                        </div>
                      </div>
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ LEVEL 3: Characters within World ═══ */}
        {selectedWorld && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-blue-300 font-mono">Characters</h2>
                {selectedWorld.description && (
                  <p className="text-slate-500 text-sm mt-1">{selectedWorld.description}</p>
                )}
              </div>
              {(selectedWorld.characters || []).length < 3 && (
                <Button
                  onClick={() => navigate('/character/create', { state: { worldId: selectedWorld.world_id, universe: selectedWorld.universe } })}
                  className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 font-mono"
                  data-testid="create-character-btn"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New Character
                </Button>
              )}
            </div>

            {(selectedWorld.characters || []).length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-12 rounded-lg text-center border border-white/5">
                <Sword className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg mb-2 font-mono">No characters yet</p>
                <p className="text-slate-500 text-sm mb-4">Create up to 3 characters in this world</p>
                <Button
                  onClick={() => navigate('/character/create', { state: { worldId: selectedWorld.world_id, universe: selectedWorld.universe } })}
                  className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 font-mono"
                >
                  <Plus className="mr-2 h-4 w-4" /> Create First Character
                </Button>
              </motion.div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(selectedWorld.characters || []).map((character, i) => (
                  <motion.div
                    key={character.character_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="glass-card rounded-lg hover:border-blue-500/40 transition-all relative group"
                    data-testid={`character-card-${i}`}
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: character.character_id, name: character.name, type: 'character' }); }}
                      className="absolute top-3 right-3 p-2 rounded-md text-red-400/40 hover:text-red-400 hover:bg-red-500/20 transition-all opacity-0 group-hover:opacity-100 z-10"
                      data-testid={`delete-character-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>

                    <div className="cursor-pointer" onClick={() => launchCharacter(character)}>
                      {character.portrait_url ? (
                        <div className="w-full h-48 overflow-hidden rounded-t-lg bg-slate-900/50">
                          <img src={character.portrait_url} alt={character.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        </div>
                      ) : (
                        <div className="w-full h-32 bg-gradient-to-br from-slate-900 to-slate-800 rounded-t-lg flex items-center justify-center">
                          <Sword className="h-10 w-10 text-slate-700" />
                        </div>
                      )}
                      <div className="p-4">
                        <h3 className="text-lg font-bold text-slate-200 mb-1 font-cinzel pr-8">{character.name}</h3>
                        <div className="flex gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-blue-500/15 text-blue-400 rounded text-xs font-mono">{character.race}</span>
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">{character.char_class}</span>
                        </div>
                        {character.backstory && (
                          <p className="text-slate-500 text-xs line-clamp-2">{character.backstory}</p>
                        )}
                      </div>
                    </div>

                    {/* Portrait controls */}
                    <div className="px-4 pb-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={async () => {
                          setGeneratingPortraitId(character.character_id);
                          try {
                            await axios.post(`${API}/characters/${character.character_id}/generate-portrait`, { image_model: dashImageModel }, { withCredentials: true, timeout: 120000 });
                            toast.success('Portrait generated!');
                            const res = await axios.get(`${API}/worlds/${selectedWorld.world_id}`, { withCredentials: true });
                            setSelectedWorld(res.data);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to generate portrait');
                          } finally {
                            setGeneratingPortraitId(null);
                          }
                        }}
                        disabled={generatingPortraitId === character.character_id}
                        className={`w-full flex items-center justify-center gap-1.5 text-xs py-2 rounded transition-all border ${
                          generatingPortraitId === character.character_id
                            ? 'bg-purple-500/10 border-purple-500/20 text-purple-300 cursor-wait'
                            : 'bg-slate-800/50 border-white/5 text-slate-400 hover:text-purple-300 hover:border-purple-500/30'
                        }`}
                      >
                        {generatingPortraitId === character.character_id ? (
                          <><RefreshCw className="h-3 w-3 animate-spin" /> Generating...</>
                        ) : character.portrait_url ? (
                          <><RefreshCw className="h-3 w-3" /> Regenerate Portrait</>
                        ) : (
                          <><ImagePlus className="h-3 w-3" /> Generate Portrait</>
                        )}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      {/* Canon Library Modal */}
      <CanonLibraryModal
        open={canonModalOpen}
        onClose={() => setCanonModalOpen(false)}
        filterUniverse={canonFilterUniverse}
      />
    </div>
  );
}
